// Slice 41 — single-select dropdown. Click-outside closes it, Escape too.

import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { Icons } from "./icons";

export interface HqSelectOption<T extends string = string> {
  value: T;
  label: string;
}

export interface HqSelectProps<T extends string = string> {
  options: HqSelectOption<T>[];
  value: T | null;
  onChange: (v: T) => void;
  placeholder?: string;
  size?: "sm" | "md" | "lg";
  style?: CSSProperties;
}

const HEIGHTS = { sm: 30, md: 34, lg: 40 } as const;

export function HqSelect<T extends string = string>({
  options,
  value,
  onChange,
  placeholder = "Select…",
  size = "md",
  style,
}: HqSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const h = HEIGHTS[size];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = options.find((o) => o.value === value);
  return (
    <div ref={ref} style={{ position: "relative", ...style }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          height: h,
          width: "100%",
          padding: "0 10px 0 12px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "var(--hq-bg-input)",
          color: "var(--hq-text)",
          border: "1px solid var(--hq-border-input)",
          borderRadius: "var(--hq-radius-lg)",
          fontSize: 12.5,
          cursor: "pointer",
          textAlign: "left",
          fontFamily: "var(--hq-font-sans)",
        }}
      >
        <span
          style={{
            flex: 1,
            color: current ? "var(--hq-text)" : "var(--hq-text-dim)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {current ? current.label : placeholder}
        </span>
        <Icons.ChevD
          width={11}
          height={11}
          style={{
            color: "var(--hq-text-dim)",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .15s",
          }}
        />
      </button>
      {open && (
        <div
          className="hq-fade-in"
          style={{
            position: "absolute",
            top: h + 4,
            left: 0,
            right: 0,
            zIndex: 50,
            background: "var(--hq-bg-raised)",
            border: "1px solid var(--hq-border-strong)",
            borderRadius: "var(--hq-radius-lg)",
            padding: 4,
            maxHeight: 280,
            overflow: "auto",
            boxShadow: "0 10px 30px rgba(0,0,0,.4)",
          }}
        >
          {options.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  textAlign: "left",
                  background: active ? "var(--hq-bg-active)" : "transparent",
                  color: "var(--hq-text)",
                  fontSize: 12.5,
                  border: "none",
                  borderRadius: "var(--hq-radius-md)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontFamily: "var(--hq-font-sans)",
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = "var(--hq-bg-input)";
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.background = "transparent";
                }}
              >
                <span style={{ flex: 1 }}>{o.label}</span>
                {active && (
                  <Icons.Check width={11} height={11} style={{ color: "var(--hq-accent)" }} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
